/**
 * The three axes of a rate-card face, in Price Seeker's own vocabulary.
 *
 * A face exists to be matched against a rate-shop result, so every axis
 * has to be something the rate shop observes. It used to be three
 * hardcoded slug lists — `prepaid`/`postpaid`, `standard`/`cgu`,
 * `canarias`/`tenerifecar` — that nothing reconciled with Price Seeker:
 * an operator could pin a face to an insurance that did not exist and
 * find out never, because a face that matches nothing fails silently.
 *
 * Now:
 *   - tariff    → `is_prepaid`, the boolean `RESULTS_CH.is_prepaid`
 *   - insurance → `ps_insurance_id`, `RESULTS_CH.id_insurance`
 *   - franchise → `ps_provider_id`, `id_master_provider`, and only the
 *     tenant's own brands — a franchise IS one of our main providers
 *     (Curro, 2026-09-23), so the list comes from the same
 *     configuration the engine reads and never from the market.
 *
 * `null` means "any" on all three. The key helpers below exist because
 * `false` and `0` are real values on these axes and falsiness is not a
 * test for absence — the same bug that made a Non-Prepaid price limit
 * score as unscoped.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { BACKEND_APIS } from '@constants'
import { setupApi } from './api'

/** Tariff is a boolean, so its list is fixed and needs no fetch. */
export const PREPAID_OPTIONS = [
	{ code: '1', name: 'Prepaid' },
	{ code: '0', name: 'Non prepaid' },
]

//  value ↔ select key. Never `||`, never `!v`: `false` is Non Prepaid
//  and `0` would be a legitimate id.
export const prepaidKey   = (v) => (v == null ? '' : (v ? '1' : '0'))
export const prepaidValue = (k) => (k === '' || k == null ? null : k === '1')
export const idKey        = (v) => (v == null ? '' : String(v))
export const idValue      = (k) => (k === '' || k == null ? null : Number(k))

const AxisCatalog = createContext({
	insurances: [], franchises: [], loading: true,
	prepaidName: () => '—', insuranceName: () => '—', franchiseName: () => '—',
})

export function AxisCatalogProvider({ children }) {
	const [state, setState] = useState({ insurances: [], franchises: [], loading: true })

	useEffect(() => {
		let alive = true
		const insurances = fetch(`${BACKEND_APIS.MASTER_DATA}/options/insurance`,
		                         { credentials: 'include' })
			.then(r => (r.ok ? r.json() : []))
			.then(rows => (Array.isArray(rows) ? rows : [])
				.map(o => ({ code: String(o.value), name: o.label })))
			.catch(() => [])
		// A tenant with no `repricing_own_provider_ids` gets an empty
		// list, and the picker says so. Falling back to every provider
		// would offer competitors' ids, which match nothing.
		const franchises = setupApi.ownProviders()
			.then(rows => (rows || []).map(p => ({ code: String(p.id), name: p.name })))
			.catch(() => [])

		Promise.all([insurances, franchises]).then(([ins, fr]) => {
			if (alive) setState({ insurances: ins, franchises: fr, loading: false })
		})
		return () => { alive = false }
	}, [])

	const value = useMemo(() => {
		const name = (list, key) => list.find(o => o.code === key)?.name
		return {
			...state,
			prepaidName:   (v) => (v == null ? '—' : (v ? 'Prepaid' : 'Non prepaid')),
			insuranceName: (v) => (v == null ? '—' : name(state.insurances, String(v)) || `insurance #${v}`),
			franchiseName: (v) => (v == null ? '—' : name(state.franchises, String(v)) || `provider #${v}`),
		}
	}, [state])

	return <AxisCatalog.Provider value={value}>{children}</AxisCatalog.Provider>
}

export function useAxisCatalog() {
	return useContext(AxisCatalog)
}

// ---------------------------------------------------------------------------
// Resolving free text to an axis value
// ---------------------------------------------------------------------------
//
// The Price Limits CSV importers read a competitor's export, which
// names brands and products in words: "Tenerifecar", "PREPAID". Those
// used to be stored verbatim because the axes were free text. They are
// ids now, so the words have to be resolved — and a word that resolves
// to nothing becomes "any" plus a warning, never a silent guess. A
// price limit scoped to the wrong brand is worse than one scoped to
// all of them.

export { resolveInsurance, resolveFranchise, resolvePrepaid } from './axesResolve'
